import * as Yup from 'yup'
import { t } from '../../i18n'
import { DosingLimits, Range } from '../shared/dosingLimits'
import { boundedNumber, whenEnabled } from '../shared/forms/yupNumbers'
import { EmergencyStepValues, msToSeconds } from './feederDosingModeValues'

type NamedField = 'pressure' | 'frequency' | 'duty_cycle' | 'air_pressure'

const message = (key: string, options?: Record<string, number>) =>
  t(`feeder_dosing_modes.validations.${key}`, options)

// A field with its own messages, so the error names it ("Pressure is required")
const named = (field: NamedField, range: Range) =>
  boundedNumber(range, {
    required: message(`${field}.required`),
    min: message(`${field}.min`, { min: range.min }),
    max: message(`${field}.max`, { max: range.max })
  })

const wholeNumber = (range: Range) => {
  const outOfRange = message('whole_number_between', { ...range })
  return boundedNumber(range, {
    required: message('required'),
    min: outOfRange,
    max: outOfRange,
    integer: outOfRange
  })
}

// Durations are entered in seconds while the limits arrive in milliseconds
const seconds = ({ min, max }: Range) => {
  const range = { min: msToSeconds(min) ?? 0, max: msToSeconds(max) ?? 0 }
  const outOfRange = message('seconds_between', { ...range })
  return boundedNumber(range, {
    required: message('required'),
    min: outOfRange,
    max: outOfRange
  })
}

export const feederDosingModeSchema = (limits: DosingLimits) => {
  const parameterFields = {
    pressure: named('pressure', limits.pressure),
    frequency: named('frequency', limits.frequency),
    duty_cycle: named('duty_cycle', limits.duty_cycle)
  }
  const parameterSchema = Yup.object(parameterFields)
  const blowerSchema = Yup.object({
    air_pressure: whenEnabled(
      'enabled',
      named('air_pressure', limits.pressure)
    ),
    activation_delay_ms: whenEnabled(
      'enabled',
      seconds(limits.blower.activation_delay_ms)
    ),
    blowing_duration_on_ms: whenEnabled(
      'enabled',
      seconds(limits.blower.blowing_duration_on_ms)
    ),
    blowing_duration_off_ms: whenEnabled(
      'enabled',
      seconds(limits.blower.blowing_duration_off_ms)
    )
  })

  return Yup.object({
    name: Yup.string().required(message('name.required')),
    high_fill_min_percent: wholeNumber(limits.fill_percent),
    medium_fill_min_percent: wholeNumber(limits.fill_percent).test(
      'below-high',
      message('medium_below_high'),
      function belowHigh(value) {
        const high = this.parent.high_fill_min_percent
        return value == null || high == null || value < high
      }
    ),
    unknown_fill_fallback: Yup.string().required(message('required')),
    levels: Yup.array().of(
      Yup.object({
        fast_parameter: parameterSchema,
        slow_parameter: parameterSchema
      })
    ),
    emergency_setting: Yup.object({
      max_attempts: wholeNumber(limits.emergency.attempts),
      residual_delivery_amount: wholeNumber(
        limits.emergency.residual_delivery_amount
      ),
      max_dosing_time_ms: seconds(limits.emergency.max_dosing_time_ms),
      low_fill_max_dosing_time_ms: seconds(
        limits.emergency.max_dosing_time_ms
      ).test(
        'not-longer',
        message('low_fill_not_longer'),
        function notLonger(value) {
          const maxDosingTime = this.parent.max_dosing_time_ms
          return (
            value == null || maxDosingTime == null || value <= maxDosingTime
          )
        }
      ),
      no_change_max_time_ms: seconds(limits.emergency.no_change_max_time_ms),
      no_change_amount: wholeNumber(limits.emergency.no_change_amount)
    }),
    emergency_steps: Yup.array().of(
      Yup.lazy((step: EmergencyStepValues) =>
        step._destroy
          ? Yup.object()
          : Yup.object({
              ...parameterFields,
              duration_ms: seconds(limits.emergency.step_duration_ms)
            })
      )
    ),
    go_back_duration_ms: whenEnabled(
      'go_back_enabled',
      seconds(limits.go_back_duration_ms)
    ),
    go_back_parameter: whenEnabled('go_back_enabled', parameterSchema),
    max_acceptable_shortfall_percent: wholeNumber(limits.shortfall_percent),
    standard_blower_setting: blowerSchema,
    emergency_blower_setting: blowerSchema
  })
}
